import Card from "components/Card/Card";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  Modal,
  Pagination,
  Row,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import {
  downloadLeadRetentionExport,
  getLeadRetentionAnalytics,
  getLeadRetentionCampaignDetails,
  getLeadRetentionCampaigns,
  getLeadRetentionCampaignStatus,
  getLeadRetentionProfile,
  getLeadRetentionProfiles,
  getLeadRetentionRefreshRun,
  previewLeadRetentionCampaign,
  sendLeadRetentionCampaign,
  startLeadRetentionRefresh,
} from "../../../api/leadRetention/leadRetention";
import { getApiErrorMessage } from "../../../utils/getApiErrorMessage";
import LoadingSpinner from "../../Components/loadingSpinner";
import "../ClaimRiskRetention/claimRiskRetention.css";
import "./leadRetention.css";

const showMeOptions = [
  { value: "", label: "All customers" },
  { value: "follow_up_today", label: "Need follow-up today" },
  { value: "one_order", label: "One order only" },
  { value: "two_orders", label: "Two orders only" },
  { value: "repeat_customers", label: "Repeat customers" },
  { value: "ordered_last_6_not_this_month", label: "Ordered recently, not this month" },
  { value: "no_order_this_month", label: "No order this month" },
  { value: "inactive_60_days", label: "Inactive 60+ days" },
  { value: "inactive_90_days", label: "Inactive 90+ days" },
  { value: "chronic_detected", label: "Chronic detected" },
  { value: "high_value", label: "High value customers" },
];

const defaultBusinessStatuses = [
  "Needs conversion",
  "Needs follow-up",
  "At risk",
  "Lapsed",
  "Chronic care",
  "Active",
];

const defaultLtvSegments = ["VIP", "High", "Medium", "Low"];
const defaultPriorities = ["High", "Medium", "Low"];
const perPageOptions = [10, 20, 30, 50].map((value) => ({ value, label: `${value} per page` }));
const emptyFilters = {
  search: "",
  organization_id: "",
  condition_name: "",
  retention_priority: "",
  business_status: "",
  ltv_segment: "",
  show_me: "",
  per_page: 20,
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-LK", {
    maximumFractionDigits: 0,
  });

const formatNumber = (value) => Number(value || 0).toLocaleString("en-LK");
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatDate = (value) => (value ? String(value).slice(0, 10) : "-");
const smsParts = (message) => ((message || "").length <= 160 ? 1 : Math.ceil((message || "").length / 153));
const selectedValue = (options, value) => options.find((option) => String(option.value) === String(value)) || options[0];
const compactObject = (value) =>
  Object.keys(value).reduce((result, key) => {
    if (value[key] !== "" && value[key] !== null && value[key] !== undefined) {
      result[key] = value[key];
    }
    return result;
  }, {});

const getInitialDateRange = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - 29);

  return {
    current_from: formatDate(from.toISOString()),
    current_to: formatDate(today.toISOString()),
  };
};

const readFilters = (search) => {
  const params = new URLSearchParams(search);

  return {
    search: params.get("search") || "",
    organization_id: params.get("organization_id") || "",
    condition_name: params.get("condition_name") || "",
    retention_priority: params.get("retention_priority") || "",
    business_status: params.get("business_status") || "",
    ltv_segment: params.get("ltv_segment") || "",
    show_me: params.get("show_me") || "",
    per_page: Number(params.get("per_page") || emptyFilters.per_page),
  };
};

const readDateRange = (search) => {
  const params = new URLSearchParams(search);
  const fallback = getInitialDateRange();

  return {
    current_from: params.get("current_from") || fallback.current_from,
    current_to: params.get("current_to") || fallback.current_to,
  };
};

const readPage = (search) => Number(new URLSearchParams(search).get("page") || 1);
const buildSelectOptions = (values, allLabel) => [{ value: "", label: allLabel }].concat((values || []).map((value) => ({ value, label: value })));
const parseLeadIds = (value) => value.split(",").map((item) => Number(item.trim())).filter((item) => item);
const isDefaultDateRange = (activeDateRange) => {
  const fallback = getInitialDateRange();

  return (
    activeDateRange.current_from === fallback.current_from &&
    activeDateRange.current_to === fallback.current_to
  );
};

const formatFilterSummary = (filters) =>
  Object.keys(filters || {})
    .filter((key) => filters[key] !== "" && filters[key] !== null && filters[key] !== undefined)
    .map((key) => `${key.replace(/_/g, " ")}: ${filters[key]}`)
    .join(", ") || "-";

const LeadRetention = ({ history, location }) => {
  const token = JSON.parse(localStorage.getItem("user"));
  const authToken = token && token.token;
  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [summary, setSummary] = useState({});
  const [analytics, setAnalytics] = useState({ current: {}, previous: {}, lift: {}, condition_breakdown: [] });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [filters, setFilters] = useState(() => readFilters(location.search));
  const [dateRange, setDateRange] = useState(() => readDateRange(location.search));
  const [filterOptions, setFilterOptions] = useState({
    retention_priorities: defaultPriorities,
    business_statuses: defaultBusinessStatuses,
    ltv_segments: defaultLtvSegments,
    condition_options: [],
    organization_options: [],
  });
  const [run, setRun] = useState(null);
  const [refreshForm, setRefreshForm] = useState({
    chunk_size: 100,
    lead_ids: "",
    force_sync_all: false,
  });
  const [exporting, setExporting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignSending, setCampaignSending] = useState(false);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [campaignMessage, setCampaignMessage] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [campaignDetailLoading, setCampaignDetailLoading] = useState(false);

  const audienceFilters = (activeFilters = filters) => {
    const { per_page, ...rest } = activeFilters;
    return compactObject(rest);
  };

  const listParams = (page = 1, activeFilters = filters) =>
    compactObject({
      ...audienceFilters(activeFilters),
      page,
      per_page: activeFilters.per_page || emptyFilters.per_page,
    });

  const analyticsParams = (activeFilters = filters, activeDateRange = dateRange) =>
    compactObject({
      ...audienceFilters(activeFilters),
      current_from: activeDateRange.current_from,
      current_to: activeDateRange.current_to,
    });

  const syncUrl = (page = 1, activeFilters = filters, activeDateRange = dateRange) => {
    const params = new URLSearchParams();

    Object.keys(audienceFilters(activeFilters)).forEach((key) => params.set(key, activeFilters[key]));
    if (Number(activeFilters.per_page || emptyFilters.per_page) !== emptyFilters.per_page) {
      params.set("per_page", activeFilters.per_page);
    }
    if (!isDefaultDateRange(activeDateRange)) {
      params.set("current_from", activeDateRange.current_from);
      params.set("current_to", activeDateRange.current_to);
    }
    if (page > 1) {
      params.set("page", page);
    }

    history.replace({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
    });
  };

  const loadProfiles = (page = 1, activeFilters = filters, activeDateRange = dateRange) => {
    syncUrl(page, activeFilters, activeDateRange);
    setLoading(true);
    setAnalyticsLoading(true);

    getLeadRetentionProfiles(authToken, listParams(page, activeFilters))
      .then((res) => {
        if (!res || !res.success) {
          return;
        }

        const responseFilters = res.data.filters || {};

        setSummary(res.data.summary || {});
        setProfiles((res.data.profiles && res.data.profiles.data) || []);
        setPagination(res.data.profiles || { current_page: 1, last_page: 1, total: 0 });
        setFilterOptions({
          retention_priorities: responseFilters.retention_priorities || defaultPriorities,
          business_statuses: responseFilters.business_statuses || defaultBusinessStatuses,
          ltv_segments: responseFilters.ltv_segments || defaultLtvSegments,
          condition_options: responseFilters.condition_options || [],
          organization_options: responseFilters.organization_options || [],
        });
      })
      .catch((error) => {
        Swal.fire("Profiles failed", getApiErrorMessage(error), "error");
      })
      .finally(() => setLoading(false));

    getLeadRetentionAnalytics(authToken, analyticsParams(activeFilters, activeDateRange))
      .then((res) => {
        if (res && res.success) {
          setAnalytics(res.data || { current: {}, previous: {}, lift: {}, condition_breakdown: [] });
        }
      })
      .catch(() => {
        setAnalytics({ current: {}, previous: {}, lift: {}, condition_breakdown: [] });
      })
      .finally(() => setAnalyticsLoading(false));
  };

  useEffect(() => {
    loadProfiles(readPage(location.search), readFilters(location.search), readDateRange(location.search));
  }, []);

  useEffect(() => {
    if (!run || !["pending", "running"].includes(run.status)) {
      return undefined;
    }

    const timer = setTimeout(() => {
      getLeadRetentionRefreshRun(authToken, run.id).then((res) => {
        if (!res || !res.success) {
          return;
        }

        setRun(res.data);
        if (res.data.status === "completed") {
          loadProfiles(1);
        }
      });
    }, 3500);

    return () => clearTimeout(timer);
  }, [run, authToken]);

  useEffect(() => {
    if (!activeCampaign || !["queued", "running"].includes(activeCampaign.status)) {
      return undefined;
    }

    const timer = setTimeout(() => {
      getLeadRetentionCampaignStatus(authToken, activeCampaign.id).then((res) => {
        if (res && res.success) {
          setActiveCampaign(res.data);
        }
      });
    }, 3500);

    return () => clearTimeout(timer);
  }, [activeCampaign, authToken]);

  const updateFilter = (key, value) => setFilters({ ...filters, [key]: value });

  const updateFilterAndLoad = (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    loadProfiles(1, nextFilters);
  };

  const updateDate = (key, value) => setDateRange({ ...dateRange, [key]: value });
  const applyFilters = () => loadProfiles(1, filters, dateRange);

  const applyQuickFilter = (nextFilterSet) => {
    const nextFilters = { ...filters, ...nextFilterSet };
    setFilters(nextFilters);
    loadProfiles(1, nextFilters, dateRange);
  };

  const resetFilters = () => {
    const nextFilters = { ...emptyFilters };
    const nextDateRange = getInitialDateRange();

    setFilters(nextFilters);
    setDateRange(nextDateRange);
    loadProfiles(1, nextFilters, nextDateRange);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    setExporting(true);
    downloadLeadRetentionExport(authToken, audienceFilters(filters))
      .then((response) => {
        downloadBlob(response.data, `lead-retention-${Date.now()}.xlsx`);
      })
      .catch((error) => {
        Swal.fire("Export failed", getApiErrorMessage(error), "error");
      })
      .finally(() => setExporting(false));
  };

  const openDetail = (leadId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedProfile(null);

    getLeadRetentionProfile(authToken, leadId)
      .then((res) => {
        if (res && res.success) {
          setSelectedProfile(res.data);
        }
      })
      .catch((error) => {
        Swal.fire("Profile failed", getApiErrorMessage(error), "error");
      })
      .finally(() => setDetailLoading(false));
  };

  const campaignPayload = (overrides = {}) =>
    compactObject({
      ...audienceFilters(filters),
      current_from: dateRange.current_from,
      current_to: dateRange.current_to,
      ...overrides,
    });

  const previewCampaign = (messageOverride) => {
    const hasOverride = messageOverride !== undefined && messageOverride !== null;

    setCampaignLoading(true);
    previewLeadRetentionCampaign(
      authToken,
      campaignPayload({
        message_body: hasOverride ? messageOverride : campaignMessage || undefined,
      }),
    )
      .then((res) => {
        if (!res || !res.success) {
          Swal.fire("Preview failed", res && res.message ? res.message : "No matching audience found.", "warning");
          return;
        }

        setCampaignPreview(res.data);
        setCampaignMessage(res.data.message_body || "");
        if (!campaignName) {
          setCampaignName(`Lead Retention SMS - ${formatDate(new Date().toISOString())}`);
        }
      })
      .catch((error) => {
        Swal.fire("Preview failed", getApiErrorMessage(error), "error");
      })
      .finally(() => setCampaignLoading(false));
  };

  const openCampaignModal = () => {
    setCampaignModalOpen(true);
    setCampaignPreview(null);
    setCampaignMessage("");
    setActiveCampaign(null);
    previewCampaign("");
  };

  const sendCampaign = () => {
    const count = campaignPreview ? campaignPreview.recipient_count : 0;

    if (!campaignMessage || campaignMessage.length < 20) {
      Swal.fire("Message is too short", "Please add a message with at least 20 characters.", "warning");
      return;
    }

    if (!count) {
      Swal.fire("No recipients", "This audience has no customers with mobile numbers.", "warning");
      return;
    }

    Swal.fire({
      title: "Send SMS campaign?",
      html: `<font size="4px">This will send a real SMS to <b>${formatNumber(count)}</b> customers.</font>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, send campaign",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      setCampaignSending(true);
      sendLeadRetentionCampaign(
        authToken,
        campaignPayload({
          campaign_name: campaignName,
          message_body: campaignMessage,
          confirm_send: true,
          max_recipients: 500,
        }),
      )
        .then((res) => {
          if (!res || !res.success) {
            Swal.fire("Campaign not sent", res && res.message ? res.message : "Please try again.", "warning");
            return;
          }

          setActiveCampaign({
            id: res.data.campaign_id,
            status: res.data.status,
            recipient_count: res.data.recipient_count,
            sent_count: 0,
            failed_count: 0,
          });
          Swal.fire("Campaign queued", "SMS sending has started in the background.", "success");
        })
        .catch((error) => {
          Swal.fire("Campaign not sent", getApiErrorMessage(error), "error");
        })
        .finally(() => setCampaignSending(false));
    });
  };

  const loadCampaignHistory = (page = 1) => {
    setHistoryLoading(true);
    getLeadRetentionCampaigns(authToken, { page })
      .then((res) => {
        if (!res || !res.success) {
          return;
        }

        setCampaignHistory((res.data.campaigns && res.data.campaigns.data) || []);
        setHistoryPagination(res.data.campaigns || { current_page: 1, last_page: 1, total: 0 });
      })
      .catch((error) => {
        Swal.fire("History failed", getApiErrorMessage(error), "error");
      })
      .finally(() => setHistoryLoading(false));
  };

  const openHistoryModal = () => {
    setHistoryModalOpen(true);
    setSelectedCampaign(null);
    setCampaignRecipients([]);
    loadCampaignHistory();
  };

  const openCampaignDetails = (campaignId) => {
    setCampaignDetailLoading(true);
    getLeadRetentionCampaignDetails(authToken, campaignId)
      .then((res) => {
        if (res && res.success) {
          setSelectedCampaign(res.data.campaign);
          setCampaignRecipients(res.data.recipients || []);
        }
      })
      .catch((error) => {
        Swal.fire("Campaign details failed", getApiErrorMessage(error), "error");
      })
      .finally(() => setCampaignDetailLoading(false));
  };

  const updateRefresh = (key, value) => setRefreshForm({ ...refreshForm, [key]: value });

  const startRefresh = () => {
    const leadIds = parseLeadIds(refreshForm.lead_ids);
    const body = leadIds.length
      ? leadIds.length === 1
        ? { lead_id: leadIds[0] }
        : { lead_ids: leadIds }
      : { all: true };

    Swal.fire({
      title: "Start refresh?",
      html: "<font size='4px'>This will queue a new lead retention refresh run.</font>",
      showCancelButton: true,
      confirmButtonText: "Start",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      startLeadRetentionRefresh(
        authToken,
        compactObject({
          ...body,
          chunk_size: Number(refreshForm.chunk_size || 100),
          force_sync_all: refreshForm.force_sync_all || undefined,
        }),
      )
        .then((res) => {
          if (!res || !res.success) {
            Swal.fire("Refresh failed", res && res.message ? res.message : "Please try again.", "warning");
            return;
          }

          const nextRun = res.data.run_id ? { ...res.data, id: res.data.run_id } : res.data;
          setRun(nextRun || null);
          if (!nextRun || !nextRun.id) {
            loadProfiles(1);
          }
        })
        .catch((error) => {
          Swal.fire("Refresh failed", getApiErrorMessage(error), "error");
        });
    });
  };

  const statusBadge = (status) => {
    const normalized = (status || "Active").toLowerCase().replace(/\s+/g, "-");
    return <span className={`status-badge status-${normalized}`}>{status || "Active"}</span>;
  };

  const renderCondition = (profile) => {
    if (profile.primary_condition) {
      return profile.primary_condition;
    }

    if (profile.conditions && profile.conditions.length) {
      return profile.conditions[0].name || "Unknown";
    }

    return "Unknown";
  };

  const renderLastOrder = (profile) => {
    if (!profile.last_order_date) {
      return "No order yet";
    }

    return profile.days_since_last_order === null || profile.days_since_last_order === undefined
      ? formatDate(profile.last_order_date)
      : `${formatDate(profile.last_order_date)} (${profile.days_since_last_order} days)`;
  };

  const actionCards = [
    {
      key: "total_profiles",
      label: "Total profiles",
      value: summary.total_profiles,
      detail: "All retained customer profiles",
      filter: { show_me: "", retention_priority: "" },
      tone: "care",
    },
    {
      key: "high_priority",
      label: "High priority",
      value: summary.high_priority,
      detail: "Needs fast outreach",
      filter: { retention_priority: "High", show_me: "" },
      tone: "urgent",
    },
    {
      key: "follow_up_today",
      label: "Follow-up today",
      value: summary.follow_up_today,
      detail: "Best immediate audience",
      filter: { show_me: "follow_up_today" },
      tone: "money",
    },
    {
      key: "at_risk_this_month",
      label: "At risk this month",
      value: summary.at_risk_this_month,
      detail: "Customers going cold",
      filter: { business_status: "At risk", show_me: "" },
      tone: "warning",
    },
    {
      key: "lapsed_customers",
      label: "Lapsed customers",
      value: summary.lapsed_customers,
      detail: "Needs win-back",
      filter: { business_status: "Lapsed", show_me: "" },
      tone: "neutral",
    },
    {
      key: "repeat_customers",
      label: "Repeat customers",
      value: summary.repeat_customers,
      detail: "Already converted",
      filter: { show_me: "repeat_customers" },
      tone: "focus",
    },
    {
      key: "one_time_customers",
      label: "One-time customers",
      value: summary.one_time_customers,
      detail: "Ready for conversion",
      filter: { show_me: "one_order" },
      tone: "warning",
    },
    {
      key: "vip_customers",
      label: "VIP customers",
      value: summary.vip_customers,
      detail: "High lifetime value",
      filter: { ltv_segment: "VIP", show_me: "" },
      tone: "care",
    },
  ];

  const organizationOptions = [{ value: "", label: "All companies" }].concat(
    (filterOptions.organization_options || []).map((item) => ({
      value: String(item.id),
      label: item.name,
    })),
  );
  const conditionOptions = buildSelectOptions(filterOptions.condition_options, "All conditions");
  const priorityOptions = buildSelectOptions(filterOptions.retention_priorities, "Any priority");
  const businessStatusOptions = buildSelectOptions(filterOptions.business_statuses, "Any status");
  const ltvOptions = buildSelectOptions(filterOptions.ltv_segments, "Any LTV");

  return (
    <div className="main-content claim-risk-retention lead-retention">
      <Grid fluid>
        <Row>
          <Col md={12}>
            <Card
              content={
                <React.Fragment>
                  <div className="engine-header">
                    <div>
                      <h4>Lead Retention</h4>
                      <p>Who needs follow-up, what they buy, and the best next retention action</p>
                    </div>
                    <div className="engine-actions">
                      <span className="sync-count-pill">
                        Rs. {formatMoney(summary.estimated_revenue_at_risk)} at risk
                      </span>
                      <Button bsStyle="primary" onClick={openCampaignModal}>
                        Run SMS Campaign
                      </Button>
                      <Button onClick={openHistoryModal}>Campaign History</Button>
                    </div>
                  </div>

                  <div className="action-grid">
                    {actionCards.map((card) => (
                      <button
                        className={`action-card action-${card.tone} ${
                          Object.keys(card.filter).every((key) => filters[key] === card.filter[key])
                            ? "is-active"
                            : ""
                        }`}
                        key={card.key}
                        onClick={() => applyQuickFilter(card.filter)}
                        type="button"
                      >
                        <span className="action-label">{card.label}</span>
                        <span className="action-value">{formatNumber(card.value)}</span>
                        <span className="action-detail">{card.detail}</span>
                      </button>
                    ))}
                  </div>

                  <Row className="simple-filter-bar">
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Show me</ControlLabel>
                      <Select
                        clearable={false}
                        onChange={(item) => updateFilterAndLoad("show_me", item.value)}
                        options={showMeOptions}
                        value={selectedValue(showMeOptions, filters.show_me)}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Company</ControlLabel>
                      <Select
                        clearable={false}
                        onChange={(item) => updateFilterAndLoad("organization_id", item.value)}
                        options={organizationOptions}
                        value={selectedValue(organizationOptions, filters.organization_id)}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Condition</ControlLabel>
                      <Select
                        clearable={false}
                        onChange={(item) => updateFilterAndLoad("condition_name", item.value)}
                        options={conditionOptions}
                        value={selectedValue(conditionOptions, filters.condition_name)}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Priority</ControlLabel>
                      <Select
                        clearable={false}
                        onChange={(item) => updateFilterAndLoad("retention_priority", item.value)}
                        options={priorityOptions}
                        value={selectedValue(priorityOptions, filters.retention_priority)}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Business status</ControlLabel>
                      <Select
                        clearable={false}
                        onChange={(item) => updateFilterAndLoad("business_status", item.value)}
                        options={businessStatusOptions}
                        value={selectedValue(businessStatusOptions, filters.business_status)}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>LTV</ControlLabel>
                      <Select
                        clearable={false}
                        onChange={(item) => updateFilterAndLoad("ltv_segment", item.value)}
                        options={ltvOptions}
                        value={selectedValue(ltvOptions, filters.ltv_segment)}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <FormGroup>
                        <ControlLabel>Search</ControlLabel>
                        <FormControl
                          onChange={(event) => updateFilter("search", event.target.value)}
                          placeholder="Name or mobile"
                          value={filters.search}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Period from</ControlLabel>
                      <FormControl
                        onChange={(event) => updateDate("current_from", event.target.value)}
                        type="date"
                        value={dateRange.current_from}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Period to</ControlLabel>
                      <FormControl
                        onChange={(event) => updateDate("current_to", event.target.value)}
                        type="date"
                        value={dateRange.current_to}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Rows</ControlLabel>
                      <Select
                        clearable={false}
                        onChange={(item) => updateFilterAndLoad("per_page", item.value)}
                        options={perPageOptions}
                        value={selectedValue(perPageOptions, filters.per_page)}
                      />
                    </Col>
                    <Col md={4} sm={12} xs={12}>
                      <ControlLabel>&nbsp;</ControlLabel>
                      <div className="filter-actions">
                        <Button bsStyle="primary" onClick={applyFilters}>
                          Apply
                        </Button>
                        <Button onClick={resetFilters}>Reset</Button>
                        <Button bsStyle="info" onClick={exportExcel}>
                          {exporting ? "Downloading..." : "Export"}
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <Row className="sync-panel">
                    <Col md={3} sm={6} xs={12}>
                      <ControlLabel>Refresh chunk size</ControlLabel>
                      <FormControl
                        max="500"
                        min="10"
                        onChange={(event) => updateRefresh("chunk_size", event.target.value)}
                        type="number"
                        value={refreshForm.chunk_size}
                      />
                    </Col>
                    <Col md={5} sm={6} xs={12}>
                      <FormGroup>
                        <ControlLabel>Specific lead ids</ControlLabel>
                        <FormControl
                          onChange={(event) => updateRefresh("lead_ids", event.target.value)}
                          placeholder="123,456,789"
                          value={refreshForm.lead_ids}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>&nbsp;</ControlLabel>
                      <div className="lead-retention-checkbox">
                        <label>
                          <input
                            checked={!!refreshForm.force_sync_all}
                            onChange={(event) => updateRefresh("force_sync_all", event.target.checked)}
                            type="checkbox"
                          />{" "}
                          Force sync all
                        </label>
                      </div>
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>&nbsp;</ControlLabel>
                      <div>
                        <Button bsStyle="primary" onClick={startRefresh}>
                          Refresh Profiles
                        </Button>
                      </div>
                    </Col>
                    {run ? (
                      <Col md={12} xs={12}>
                        <div className="sync-status">
                          Run #{run.id}: {run.status} - {formatNumber(run.processed_leads || 0)}/
                          {formatNumber(run.total_leads || 0)} leads ({Number(run.progress_percentage || 0).toFixed(2)}%)
                          {run.error_message ? ` (${run.error_message})` : ""}
                        </div>
                      </Col>
                    ) : null}
                  </Row>

                  <div className="analytics-card">
                    <div className="analytics-header">
                      <div>
                        <h5>Retention analytics</h5>
                        <p>Current period performance, previous period comparison, and lift</p>
                      </div>
                      <div className="analytics-date">
                        {dateRange.current_from} to {dateRange.current_to}
                      </div>
                    </div>

                    {analyticsLoading ? (
                      <LoadingSpinner />
                    ) : (
                      <React.Fragment>
                        <div className="campaign-strip">
                          <div>
                            <span>Current revenue</span>
                            <b>Rs. {formatMoney(analytics.current && analytics.current.revenue)}</b>
                            <small>{formatNumber(analytics.current && analytics.current.orders)} orders</small>
                          </div>
                          <div>
                            <span>Current retention rate</span>
                            <b>{formatPercent(analytics.current && analytics.current.retention_rate)}</b>
                            <small>{formatNumber(analytics.qualified_customers)} qualified customers</small>
                          </div>
                          <div>
                            <span>Repeat order rate</span>
                            <b>{formatPercent(analytics.current && analytics.current.repeat_order_rate)}</b>
                            <small>{formatNumber(analytics.current && analytics.current.repeat_customers)} repeat customers</small>
                          </div>
                          <div>
                            <span>Previous revenue</span>
                            <b>Rs. {formatMoney(analytics.previous && analytics.previous.revenue)}</b>
                            <small>{formatPercent(analytics.previous && analytics.previous.retention_rate)} retention rate</small>
                          </div>
                        </div>

                        <div className="lift-grid">
                          <div>
                            <span>Revenue lift</span>
                            <b>{formatPercent(analytics.lift && analytics.lift.revenue_lift_percentage)}</b>
                          </div>
                          <div>
                            <span>Order lift</span>
                            <b>{formatPercent(analytics.lift && analytics.lift.order_lift_percentage)}</b>
                          </div>
                          <div>
                            <span>Retention lift</span>
                            <b>{Number((analytics.lift && analytics.lift.retention_rate_lift_points) || 0).toFixed(2)} pts</b>
                          </div>
                          <div>
                            <span>Repeat rate lift</span>
                            <b>{Number((analytics.lift && analytics.lift.repeat_rate_lift_points) || 0).toFixed(2)} pts</b>
                          </div>
                        </div>

                        <Table className="analytics-table" responsive>
                          <thead>
                            <tr>
                              <th>Condition</th>
                              <th>Customers</th>
                              <th>Orders</th>
                              <th>Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(analytics.condition_breakdown || []).length === 0 ? (
                              <tr>
                                <td colSpan="4">
                                  <center>No analytics data found</center>
                                </td>
                              </tr>
                            ) : (
                              (analytics.condition_breakdown || []).map((item) => (
                                <tr key={item.condition_name}>
                                  <td>{item.condition_name}</td>
                                  <td>{formatNumber(item.customers)}</td>
                                  <td>{formatNumber(item.orders)}</td>
                                  <td>Rs. {formatMoney(item.revenue)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </React.Fragment>
                    )}
                  </div>

                  <div className="table-status-bar">
                    <span>
                      Showing {formatNumber(pagination.total)} lead retention profiles. Revenue last 30 days: Rs.{" "}
                      {formatMoney(summary.revenue_last_30_days)}
                    </span>
                    <div className="table-actions">
                      <span>Rs. {formatMoney(summary.total_revenue)} lifetime revenue</span>
                    </div>
                  </div>

                  {loading ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="lead-retention-table-wrap">
                      <Table className="retention-table lead-retention-table" hover>
                        <thead>
                          <tr>
                            <th>Customer Name</th>
                            <th>Mobile</th>
                            <th>Company</th>
                            <th>Business Status</th>
                            <th>Customer Segment</th>
                            <th>Primary Condition</th>
                            <th>Total Orders</th>
                            <th>Total Revenue</th>
                            <th>Average Order Value</th>
                            <th>Last Order Date</th>
                            <th>Days Since Last Order</th>
                            <th>LTV Segment</th>
                            <th>Retention Priority</th>
                            <th>One-time Order Owner</th>
                            <th>Recommended Next Step</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profiles.length === 0 ? (
                            <tr>
                              <td colSpan="15">
                                <center>No profiles match this view</center>
                              </td>
                            </tr>
                          ) : (
                            profiles.map((profile) => (
                              <tr key={profile.id} onClick={() => openDetail(profile.lead_id)}>
                                <td>
                                  <b>{profile.lead ? profile.lead.name : "-"}</b>
                                </td>
                                <td>{(profile.lead && profile.lead.mobile_no) || "-"}</td>
                                <td>{(profile.lead && profile.lead.b2b && profile.lead.b2b.company_name) || "-"}</td>
                                <td>{statusBadge(profile.business_status)}</td>
                                <td>{profile.customer_segment || "-"}</td>
                                <td>{renderCondition(profile)}</td>
                                <td>{formatNumber(profile.total_orders)}</td>
                                <td>Rs. {formatMoney(profile.total_order_value)}</td>
                                <td>Rs. {formatMoney(profile.average_order_value)}</td>
                                <td>{renderLastOrder(profile)}</td>
                                <td>{profile.days_since_last_order === null || profile.days_since_last_order === undefined ? "-" : formatNumber(profile.days_since_last_order)}</td>
                                <td>{profile.ltv_segment || "-"}</td>
                                <td>{profile.retention_priority || "-"}</td>
                                <td>{profile.one_time_order_owner_name || profile.one_time_sales_user_name || "-"}</td>
                                <td className="next-step-cell">{profile.recommended_next_step || "-"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  <Pagination
                    activePage={pagination.current_page || 1}
                    boundaryLinks
                    ellipsis
                    first
                    items={pagination.last_page || 1}
                    last
                    maxButtons={5}
                    next
                    onSelect={(page) => loadProfiles(page)}
                    prev
                  />

                  <Modal bsSize="large" onHide={() => setDetailOpen(false)} show={detailOpen}>
                    <Modal.Header closeButton>
                      <Modal.Title>Lead retention profile</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {detailLoading ? (
                        <LoadingSpinner />
                      ) : selectedProfile ? (
                        <React.Fragment>
                          <div className="detail-card">
                            <div className="detail-card-header">
                              <div>
                                <h5>{selectedProfile.lead ? selectedProfile.lead.name : "-"}</h5>
                                <p>
                                  {(selectedProfile.lead && selectedProfile.lead.mobile_no) || "-"} |{" "}
                                  {(selectedProfile.lead &&
                                    selectedProfile.lead.b2b &&
                                    selectedProfile.lead.b2b.company_name) ||
                                    "No company"}
                                </p>
                              </div>
                              <div className="detail-badges">
                                {statusBadge(selectedProfile.business_status)}
                                <span className="detail-chip">{selectedProfile.ltv_segment || "No LTV"}</span>
                                <span className="detail-chip">{selectedProfile.retention_priority || "No priority"}</span>
                              </div>
                            </div>

                            <div className="campaign-strip">
                              <div>
                                <span>Total revenue</span>
                                <b>Rs. {formatMoney(selectedProfile.total_order_value)}</b>
                              </div>
                              <div>
                                <span>Total orders</span>
                                <b>{formatNumber(selectedProfile.total_orders)}</b>
                              </div>
                              <div>
                                <span>Average order value</span>
                                <b>Rs. {formatMoney(selectedProfile.average_order_value)}</b>
                              </div>
                              <div>
                                <span>Days since last order</span>
                                <b>
                                  {selectedProfile.days_since_last_order === null ||
                                  selectedProfile.days_since_last_order === undefined
                                    ? "-"
                                    : formatNumber(selectedProfile.days_since_last_order)}
                                </b>
                              </div>
                            </div>

                            <div className="detail-grid">
                              <div className="detail-section">
                                <h6>Follow-up reason</h6>
                                <p>{selectedProfile.follow_up_reason || "-"}</p>
                              </div>
                              <div className="detail-section">
                                <h6>Recommended next step</h6>
                                <p>{selectedProfile.recommended_next_step || "-"}</p>
                              </div>
                              <div className="detail-section">
                                <h6>One-time order owner</h6>
                                <p>{selectedProfile.one_time_order_owner_name || selectedProfile.one_time_sales_user_name || "-"}</p>
                              </div>
                              <div className="detail-section">
                                <h6>Customer segment</h6>
                                <p>{selectedProfile.customer_segment || "-"}</p>
                              </div>
                            </div>

                            <Table className="analytics-table" responsive>
                              <thead>
                                <tr>
                                  <th>Condition</th>
                                  <th>Confidence</th>
                                  <th>Matched Orders</th>
                                  <th>Spend</th>
                                  <th>Last Order</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(selectedProfile.conditions || []).length === 0 ? (
                                  <tr>
                                    <td colSpan="5">
                                      <center>No condition data found</center>
                                    </td>
                                  </tr>
                                ) : (
                                  (selectedProfile.conditions || []).map((condition) => (
                                    <tr key={condition.id}>
                                      <td>{condition.name}</td>
                                      <td>{condition.confidence || "-"}</td>
                                      <td>{formatNumber(condition.matched_orders_count)}</td>
                                      <td>Rs. {formatMoney(condition.total_spend)}</td>
                                      <td>{formatDate(condition.last_order_date)}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </Table>
                          </div>
                        </React.Fragment>
                      ) : (
                        <p>No profile selected.</p>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button onClick={() => setDetailOpen(false)}>Close</Button>
                    </Modal.Footer>
                  </Modal>

                  <Modal bsSize="large" onHide={() => setCampaignModalOpen(false)} show={campaignModalOpen}>
                    <Modal.Header closeButton>
                      <Modal.Title>Run lead retention SMS campaign</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {campaignLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <React.Fragment>
                          <div className="campaign-builder-summary">
                            <div>
                              <span>Audience</span>
                              <b>{formatNumber(campaignPreview && campaignPreview.recipient_count)}</b>
                              <small>customers with mobile numbers</small>
                            </div>
                            <div>
                              <span>SMS length</span>
                              <b>{campaignMessage.length} chars</b>
                              <small>{smsParts(campaignMessage)} SMS part(s)</small>
                            </div>
                            <div>
                              <span>Mode</span>
                              <b>Dialog SMS</b>
                              <small>queued after confirmation</small>
                            </div>
                          </div>

                          <FormGroup>
                            <ControlLabel>Campaign name</ControlLabel>
                            <FormControl
                              onChange={(event) => setCampaignName(event.target.value)}
                              placeholder="Campaign name"
                              value={campaignName}
                            />
                          </FormGroup>

                          <FormGroup>
                            <ControlLabel>SMS body</ControlLabel>
                            <FormControl
                              componentClass="textarea"
                              onChange={(event) => setCampaignMessage(event.target.value)}
                              placeholder="Write the SMS message"
                              rows={5}
                              value={campaignMessage}
                            />
                            <p className="campaign-helper">
                              You can use {"{{name}}"}, {"{{first_name}}"}, {"{{condition}}"}, {"{{company}}"}, {"{{last_order_date}}"}.
                            </p>
                          </FormGroup>

                          <div className="campaign-preview-actions">
                            <Button onClick={() => previewCampaign(campaignMessage)}>Update preview</Button>
                          </div>

                          <div className="campaign-samples">
                            <h5>Sample preview</h5>
                            {campaignPreview &&
                            campaignPreview.sample_recipients &&
                            campaignPreview.sample_recipients.length ? (
                              campaignPreview.sample_recipients.map((recipient) => (
                                <div className="campaign-sample" key={recipient.lead_id}>
                                  <b>
                                    {recipient.name} - {recipient.mobile_no}
                                  </b>
                                  <p>{recipient.message_preview}</p>
                                </div>
                              ))
                            ) : (
                              <p className="campaign-helper">No matching recipients with mobile numbers.</p>
                            )}
                          </div>

                          {activeCampaign ? (
                            <div className="campaign-status-panel">
                              <b>Campaign #{activeCampaign.id}</b> is {activeCampaign.status}. Sent{" "}
                              {formatNumber(activeCampaign.sent_count || 0)} of{" "}
                              {formatNumber(activeCampaign.recipient_count || 0)}. Failed{" "}
                              {formatNumber(activeCampaign.failed_count || 0)}.
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button onClick={() => setCampaignModalOpen(false)}>Close</Button>
                      <Button
                        bsStyle="primary"
                        disabled={campaignLoading || campaignSending || !(campaignPreview && campaignPreview.recipient_count)}
                        onClick={sendCampaign}
                      >
                        {campaignSending ? "Queueing..." : "Send Campaign"}
                      </Button>
                    </Modal.Footer>
                  </Modal>

                  <Modal bsSize="large" onHide={() => setHistoryModalOpen(false)} show={historyModalOpen}>
                    <Modal.Header closeButton>
                      <Modal.Title>Campaign history</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {historyLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <React.Fragment>
                          <Table className="campaign-history-table" hover responsive>
                            <thead>
                              <tr>
                                <th>Campaign</th>
                                <th>Status</th>
                                <th>Audience</th>
                                <th>Sent</th>
                                <th>Failed</th>
                                <th>Conversion</th>
                                <th>Revenue after</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {campaignHistory.length === 0 ? (
                                <tr>
                                  <td colSpan="8">
                                    <center>No campaigns found</center>
                                  </td>
                                </tr>
                              ) : (
                                campaignHistory.map((campaign) => (
                                  <tr key={campaign.id}>
                                    <td>
                                      <b>{campaign.name}</b>
                                      <br />
                                      <small>{formatDate(campaign.created_at)}</small>
                                    </td>
                                    <td>{campaign.status}</td>
                                    <td>{formatNumber(campaign.recipient_count)}</td>
                                    <td>{formatNumber(campaign.sent_count)}</td>
                                    <td>{formatNumber(campaign.failed_count)}</td>
                                    <td>{formatPercent(campaign.conversion_rate_after)}</td>
                                    <td>Rs. {formatMoney(campaign.revenue_after)}</td>
                                    <td>
                                      <Button bsSize="xsmall" onClick={() => openCampaignDetails(campaign.id)}>
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>

                          <Pagination
                            activePage={historyPagination.current_page || 1}
                            boundaryLinks
                            ellipsis
                            first
                            items={historyPagination.last_page || 1}
                            last
                            maxButtons={5}
                            next
                            onSelect={(page) => loadCampaignHistory(page)}
                            prev
                          />

                          {campaignDetailLoading ? <LoadingSpinner /> : null}

                          {selectedCampaign ? (
                            <div className="campaign-detail-panel">
                              <div className="campaign-detail-header">
                                <div>
                                  <h5>{selectedCampaign.name}</h5>
                                  <p>{selectedCampaign.message_body}</p>
                                </div>
                                <div>
                                  <b>{formatPercent(selectedCampaign.conversion_rate_after)}</b>
                                  <small>{formatNumber(selectedCampaign.order_count_after)} orders after campaign</small>
                                </div>
                              </div>

                              <div className="detail-grid">
                                <div className="detail-section">
                                  <h6>Filters used</h6>
                                  <p>{formatFilterSummary(selectedCampaign.audience_filter)}</p>
                                </div>
                                <div className="detail-section">
                                  <h6>Delivery stats</h6>
                                  <p>
                                    Sent {formatNumber(selectedCampaign.sent_count)} / Failed{" "}
                                    {formatNumber(selectedCampaign.failed_count)}
                                  </p>
                                </div>
                                <div className="detail-section">
                                  <h6>Revenue after</h6>
                                  <p>Rs. {formatMoney(selectedCampaign.revenue_after)}</p>
                                </div>
                                <div className="detail-section">
                                  <h6>Ordering customers after</h6>
                                  <p>{formatNumber(selectedCampaign.ordering_customers_after)}</p>
                                </div>
                              </div>

                              <Table className="campaign-recipient-table" condensed responsive>
                                <thead>
                                  <tr>
                                    <th>Customer</th>
                                    <th>Company</th>
                                    <th>Condition</th>
                                    <th>Status</th>
                                    <th>Sent at</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {campaignRecipients.map((recipient) => (
                                    <tr key={recipient.id}>
                                      <td>
                                        <b>{recipient.customer_name}</b>
                                        <br />
                                        <small>{recipient.mobile_no}</small>
                                      </td>
                                      <td>{recipient.company_name || "-"}</td>
                                      <td>{recipient.condition_name || "-"}</td>
                                      <td>{recipient.status}</td>
                                      <td>{formatDate(recipient.sent_at)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button onClick={() => loadCampaignHistory(historyPagination.current_page || 1)}>Refresh</Button>
                      <Button onClick={() => setHistoryModalOpen(false)}>Close</Button>
                    </Modal.Footer>
                  </Modal>
                </React.Fragment>
              }
            />
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

export default LeadRetention;
